import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { useLang } from "@/context/LangContext";
import { CheckCircle, ArrowRight, ArrowLeft, Lightning, ShieldCheck, XCircle, X } from "@phosphor-icons/react";
import { openPaddleCheckout } from "@/lib/paddle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = () => {
  const token = localStorage.getItem("esg_token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export default function BillingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, isRtl } = useLang();
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const qs = new URLSearchParams(location.search);
  const [successFlash, setSuccessFlash] = useState(qs.get("subscription") === "success");
  const [confirming, setConfirming] = useState(false);
  // Two-stage signup (Feb 2026): if the user is missing company /
  // role / phone (because they signed up via the simplified Stage-1
  // form), collect them in this pre-checkout modal before opening
  // Paddle. Skips the modal for users who already provided the data
  // (grandfathered accounts, admin-created accounts, imported users).
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileForm, setProfileForm] = useState({ company_name: "", role_title: "", phone: "" });
  const [profileError, setProfileError] = useState("");
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;

  const fetchAll = async () => {
    try {
      const [s, u] = await Promise.all([
        axios.get(`${API}/subscription/status`, opts()),
        axios.get(`${API}/auth/me`, opts()).catch(() => ({ data: null })),
      ]);
      setStatus(s.data);
      setMe(u.data);
    } catch {
      setError("Failed to load billing info. Please refresh.");
    }
  };

  // On return from Paddle (successUrl redirect), refresh status so we see ACTIVE
  // as soon as the webhook fires (usually within 1-2 seconds).
  useEffect(() => {
    const isReturn = qs.get("subscription") === "success";
    if (isReturn) {
      setConfirming(true);
      // Poll status for up to 20s waiting for the webhook to flip us to active
      let tries = 0;
      const poll = async () => {
        tries++;
        await fetchAll();
        if (tries < 10) {
          setTimeout(poll, 2000);
        } else {
          setConfirming(false);
        }
      };
      poll();
    } else {
      fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isProfileIncomplete = () => {
    if (!me) return true;
    return !(me.company_name && String(me.company_name).trim()) ||
           !(me.phone       && String(me.phone).trim());
  };

  const launchPaddle = async (bodyPayload) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/subscription/checkout`, bodyPayload || {}, opts());
      if (!data?.transaction_id) throw new Error("Missing transaction id from backend");
      await openPaddleCheckout({
        transactionId: data.transaction_id,
        onCompleted: () => {
          setSuccessFlash(true);
          setBusy(false);
          fetchAll();
          // Bring the newly-paid user straight to their now-unlocked
          // dashboard after they see the success flash for a moment.
          setTimeout(() => navigate("/dashboard?welcome=paid"), 3500);
        },
        onClosed: () => {
          setBusy(false);
        },
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not start checkout. Please try again or email info@architaly.green.");
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    if (busy) return;
    if (isProfileIncomplete()) {
      // Prefill any fields we already have on the user record
      setProfileForm({
        company_name: (me && me.company_name) || "",
        role_title:   (me && me.role_title)   || "",
        phone:        (me && me.phone)        || "",
      });
      setProfileError("");
      setShowProfileModal(true);
      return;
    }
    await launchPaddle({});
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const cn = profileForm.company_name.trim();
    const ph = profileForm.phone.trim();
    if (!cn) { setProfileError(T("Please enter your company name.", "يُرجى إدخال اسم الشركة.")); return; }
    if (ph.replace(/\D/g, "").length < 6) { setProfileError(T("Please enter a valid phone number.", "يُرجى إدخال رقم هاتف صحيح.")); return; }
    setProfileError("");
    setShowProfileModal(false);
    // role_title is now optional. Send whatever we have (may be empty).
    await launchPaddle({ company_name: cn, role_title: (profileForm.role_title || "").trim(), phone: ph });
  };

  const handleCancel = async () => {
    if (!window.confirm(lang === "ar" ? "هل تريد فعلاً الإلغاء؟ ستحتفظ بالوصول حتى نهاية الفترة الحالية." : "Are you sure? You'll keep access until the end of the current period.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, opts());
      await fetchAll();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not cancel. Please email info@architaly.green.");
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return <div className="min-h-screen bg-[#070709] text-slate-100 flex items-center justify-center">Loading…</div>;
  }

  const isAr = lang === "ar";
  const T = (en, ar) => isAr ? ar : en;

  // Pre-checkout profile modal (Stage-2 signup capture)
  const profileModal = showProfileModal ? (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-sm"
      data-testid="checkout-profile-modal"
      onClick={() => { if (!busy) setShowProfileModal(false); }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0F1013] border border-[#01AF9B]/30 rounded-2xl max-w-md w-full p-6 sm:p-7 shadow-2xl"
      >
        <button
          onClick={() => setShowProfileModal(false)}
          data-testid="checkout-profile-close"
          className={`absolute top-3 ${isRtl ? "left-3" : "right-3"} text-slate-400 hover:text-white p-1.5 rounded-full hover:bg-white/5`}
          aria-label={T("Close", "إغلاق")}
        >
          <X size={20} weight="bold" />
        </button>
        <h3 className="font-['Outfit'] text-lg sm:text-xl font-bold text-white mb-1.5">
          {T("A few last details before checkout", "بضع تفاصيل أخيرة قبل الدفع")}
        </h3>
        <p className="text-xs sm:text-sm text-slate-400 mb-5">
          {T("We use these on your invoices, receipts and any support you may need.",
             "نستخدم هذه البيانات على فواتيرك وإيصالاتك وأي دعم قد تحتاجه.")}
        </p>
        <form onSubmit={handleProfileSubmit} className="space-y-3.5">
          <div>
            <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">
              {T("Company name", "اسم الشركة")} *
            </label>
            <input
              type="text"
              value={profileForm.company_name}
              onChange={(e) => setProfileForm({ ...profileForm, company_name: e.target.value })}
              required
              data-testid="checkout-profile-company"
              className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all"
              placeholder={T("Your company name", "اسم شركتك")}
            />
          </div>
          <div>
            <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">
              {T("Role / Job title", "الدور / المسمّى الوظيفي")} *
            </label>
            <input
              type="text"
              value={profileForm.role_title}
              onChange={(e) => setProfileForm({ ...profileForm, role_title: e.target.value })}
              required
              data-testid="checkout-profile-role"
              className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all"
              placeholder={T("e.g. CEO, ESG Manager", "مثلاً: الرئيس التنفيذي، مدير ESG")}
            />
          </div>
          <div>
            <label className="font-['Outfit'] text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 block">
              {T("Phone number", "رقم الهاتف")} *
            </label>
            <input
              type="tel"
              value={profileForm.phone}
              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              required
              data-testid="checkout-profile-phone"
              className="w-full bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none transition-all"
              placeholder="+971 ..."
              dir="ltr"
            />
          </div>
          {profileError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2.5 text-red-400 text-xs" data-testid="checkout-profile-error">
              {profileError}
            </div>
          )}
          <button
            type="submit"
            data-testid="checkout-profile-continue"
            className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-bold py-3 rounded-lg transition-all inline-flex items-center gap-2 justify-center"
          >
            <Lightning size={17} weight="fill" />
            {T("Continue to secure payment", "المتابعة إلى الدفع الآمن")}
          </button>
          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            {T("Payments are processed by Paddle. You will be redirected to Paddle's secure overlay next.",
               "تُعالج المدفوعات بواسطة Paddle. سيتم توجيهك إلى صفحة Paddle الآمنة بعد ذلك.")}
          </p>
        </form>
      </div>
    </div>
  ) : null;

  return (
    <>
    {profileModal}
    <div className="min-h-screen bg-[#070709] text-slate-100">
      {/* NAV */}
      <nav className="sticky top-0 z-50 bg-[#070709]/90 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-3">
            <LangSwitch />
            <button onClick={() => navigate("/dashboard")} data-testid="billing-back-dashboard"
              className="font-['Outfit'] text-xs sm:text-sm font-semibold text-slate-300 hover:text-white inline-flex items-center gap-1.5">
              <BackIcon size={16} /> {T("Dashboard", "لوحة التحكّم")}
            </button>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16" data-testid="billing-page">
        {successFlash && (
          <div className="mb-6 bg-[#01AF9B]/15 border border-[#01AF9B]/40 rounded-xl p-4 flex items-center gap-3" data-testid="billing-success-flash">
            <CheckCircle size={22} weight="fill" className="text-[#01AF9B]" />
            <p className="text-sm text-[#01AF9B] font-['Outfit'] font-semibold">
              {T("Payment received! Your subscription is now active.", "تم استلام الدفعة! اشتراكك مفعَّل الآن.")}
            </p>
          </div>
        )}

        <div className="font-['Outfit'] text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-3">
          {T("Billing", "الفوترة")}
        </div>
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
          {T("Subscription", "اشتراكك")}
        </h1>

        {/* STATUS CARD */}
        <div className="bg-[#0F1115] border border-slate-800/60 rounded-2xl p-6 sm:p-8 mb-6" data-testid="billing-status-card">
          {status.grandfathered ? (
            <div className="flex items-start gap-3">
              <ShieldCheck size={24} weight="fill" className="text-[#01AF9B] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-['Outfit'] text-lg font-bold text-white mb-1">
                  {T("Lifetime free access", "وصول مجاني مدى الحياة")}
                </h2>
                <p className="text-sm text-slate-300">
                  {T("Your account is grandfathered, you can generate unlimited ESG reports for free, forever.",
                     "حسابك مُعفى من الدفع، يمكنك إنشاء تقارير ESG غير محدودة مجّاناً وللأبد.")}
                </p>
              </div>
            </div>
          ) : status.active ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={20} weight="fill" className="text-[#01AF9B]" />
                <span className="font-['Outfit'] text-sm font-bold text-[#01AF9B] uppercase tracking-wider">
                  {T("Active", "نشط")}
                </span>
              </div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">
                {T("Annual Plan, $", "خطة سنوية، ")}{status.next_charge_amount ?? 499}{T("/year", "/سنوياً")}
              </h2>
              {status.current_period_end && (
                <p className="text-sm text-slate-400 mb-4">
                  {status.cancel_at_period_end
                    ? T(`Access ends on ${new Date(status.current_period_end).toLocaleDateString()}, no further charges.`,
                        `ينتهي الوصول في ${new Date(status.current_period_end).toLocaleDateString('ar')}, لا مزيد من الرسوم.`)
                    : T(`Renews automatically on ${new Date(status.current_period_end).toLocaleDateString()}`,
                        `يتجدّد تلقائياً في ${new Date(status.current_period_end).toLocaleDateString('ar')}`)}
                </p>
              )}
              {!status.cancel_at_period_end && (
                <button onClick={handleCancel} disabled={busy} data-testid="billing-cancel-btn"
                  className="text-sm text-slate-400 hover:text-red-400 underline transition-colors">
                  {T("Cancel subscription", "إلغاء الاشتراك")}
                </button>
              )}
            </>
          ) : status.status === "past_due" ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <XCircle size={20} weight="fill" className="text-red-400" />
                <span className="font-['Outfit'] text-sm font-bold text-red-400 uppercase tracking-wider">
                  {T("Payment failed", "فشلت الدفعة")}
                </span>
              </div>
              <p className="text-sm text-slate-300 mb-4">
                {T("Your last charge was declined. Please update your payment method to continue.",
                   "تم رفض الدفعة الأخيرة. يُرجى تحديث طريقة الدفع للمتابعة.")}
              </p>
              <button onClick={handleCheckout} disabled={busy} data-testid="billing-update-payment"
                className="bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-bold py-3 px-6 rounded-lg inline-flex items-center gap-2">
                {T("Update payment", "تحديث الدفع")} <ArrowRight size={18} className={isRtl ? "rotate-180" : ""} />
              </button>
            </>
          ) : (
            // No subscription → show offer
            <>
              <div className="bg-[#F97316] text-white text-xs sm:text-sm font-['Outfit'] font-bold tracking-wide px-4 py-2 rounded-lg inline-block mb-4 uppercase">
                {T("Limited offer · 50% OFF until October 31, 2026", "عرض محدود · خصم 50٪ حتى 31 أكتوبر 2026")}
              </div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-2">
                ${status.next_charge_amount ?? 499}<span className="text-slate-400 text-lg font-normal">{T("/year", "/سنوياً")}</span>
              </h2>
              <p className="text-sm text-slate-400 mb-6">
                {(status.next_charge_amount ?? 499) === 499
                  ? T("Discounted from $999/year. Flat $499/year with the launch discount until October 31, 2026.",
                       "بدلاً من 999 $/سنوياً. سعر ثابت 499 $/سنوياً مع خصم الإطلاق حتى 31 أكتوبر 2026.")
                  : T("Annual plan, billed yearly.", "خطة سنوية، فوترة سنوية.")}
              </p>

              <ul className="space-y-2.5 mb-6 text-sm text-slate-300">
                {[
                  T("Unlimited ESG reports", "تقارير ESG غير محدودة"),
                  T("60+ page professional PDF (English & Arabic)", "ملف PDF احترافي 60+ صفحة (إنجليزي وعربي)"),
                  T("AI-powered narrative (Claude)", "محتوى مُولَّد بالذكاء الاصطناعي (Claude)"),
                  T("GRI / TCFD / ADX alignment", "توافق مع GRI / TCFD / ADX"),
                  T("Brand customization (logo + colors)", "تخصيص العلامة (شعار + ألوان)"),
                  T("Cancel anytime before renewal", "إلغاء في أيّ وقت قبل التجديد"),
                ].map((f, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CheckCircle size={16} weight="fill" className="text-[#01AF9B] flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>

              <button onClick={handleCheckout} disabled={busy} data-testid="billing-subscribe-btn"
                className="w-full sm:w-auto bg-[#01AF9B] hover:bg-[#018F7E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-['Outfit'] font-bold text-base py-3.5 px-7 rounded-xl transition-all inline-flex items-center gap-2 justify-center">
                <Lightning size={18} weight="fill" />
                {busy
                  ? T("Opening secure checkout…", "جارٍ فتح صفحة الدفع الآمنة…")
                  : T(`Subscribe, $${status.next_charge_amount ?? 499}/year`, `اشترك, ${status.next_charge_amount ?? 499} $/سنوياً`)}
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-sm text-red-300" data-testid="billing-error">
            {error}
          </div>
        )}

        <p className="text-xs text-slate-500 mt-8 leading-relaxed">
          {T(
            "Payments are securely processed by Paddle, our Merchant of Record. All major cards, Apple Pay, Google Pay and PayPal are supported. Read our ",
            "تُعالج المدفوعات بأمان من قِبل Paddle، تاجر السجل لدينا. يتم قبول جميع البطاقات الرئيسية وApple Pay وGoogle Pay وPayPal. اطّلع على "
          )}
          <button onClick={() => navigate("/terms")} className="text-[#01AF9B] hover:underline">{T("Terms", "الشروط")}</button>{T(", ", " · ")}
          <button onClick={() => navigate("/privacy")} className="text-[#01AF9B] hover:underline">{T("Privacy", "الخصوصية")}</button>{T(", and ", " · ")}
          <button onClick={() => navigate("/refund")} className="text-[#01AF9B] hover:underline">{T("Refund Policy", "سياسة الاسترداد")}</button>.
        </p>
      </article>
    </div>
    </>
  );
}
